import { utcToZonedTime, format as formatTz } from 'date-fns-tz'
import format from 'date-fns/format'
import isToday from 'date-fns/isToday'
import isTomorrow from 'date-fns/isTomorrow'
import parseISO from 'date-fns/parseISO'
import {
  format as i18nFormat,
  DateFormatOptions
} from '@toasttab/buffet-pui-date-utilities'
import { checkLocale } from '@toasttab/buffet-pui-locale-utilities'
import { t } from 'src/translations/src'

export const dateToDayString = (
  dateObj: Date,
  formatString: string = 'EEE, M/d'
): string => {
  return isToday(dateObj)
    ? t('fulfillment-general.today')
    : isTomorrow(dateObj)
    ? t('fulfillment-general.tomorrow')
    : format(dateObj, formatString)
}

export const toDayString = (
  date: string,
  formatString: string = 'EEE, M/d'
): string => dateToDayString(parseISO(date), formatString)

export const toLocalTimeWithTimezone = (
  date: string,
  tz: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  const dateObj = parseISO(date)
  const zonedDate = utcToZonedTime(dateObj, tz)
  return formatTz(zonedDate, 'h:mm aa z', { timeZone: tz })
}

export const toLocalTime = (
  date: string,
  tz: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  const dateObj = parseISO(date)
  const zonedDate = utcToZonedTime(dateObj, tz)
  return formatTz(zonedDate, 'h:mm aa', { timeZone: tz })
}

export const toLocalDateTime = (
  dateObj: Date,
  tz: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  showTimezone: boolean = true
): string => {
  const zonedDate = utcToZonedTime(dateObj, tz)
  const formatStr = showTimezone ? 'h:mm aa z' : 'h:mm aa'
  return (
    dateToDayString(zonedDate) +
    ', ' +
    formatTz(zonedDate, formatStr, { timeZone: tz })
  )
}

export const toI18nDateTime = (
  dateObj: Date,
  formatString: string,
  locale: string = Intl.DateTimeFormat().resolvedOptions().locale,
  tz: string | undefined = Intl.DateTimeFormat().resolvedOptions().timeZone
): string | null => {
  const options: DateFormatOptions = {
    timeZone: tz,
    locale: checkLocale(locale) as DateFormatOptions['locale']
  }
  return i18nFormat(dateObj, formatString, options)
}

export const toFutureString = (
  date: string,
  tz: string = Intl.DateTimeFormat().resolvedOptions().timeZone
): string => {
  const dateObj = parseISO(date)
  const zonedDate = utcToZonedTime(dateObj, tz)

  // eslint-disable-next-line quotes
  return formatTz(zonedDate, "EEE, M/d 'at' h:mm aa z", { timeZone: tz })
}

export const formatMDY = (date: Date) => format(date, 'MM/dd/yyyy')

export const getDiningBehaviorToggleLabel = (minimumTime: number): string => {
  if (minimumTime !== null) {
    const earliestFulfillmentDate = getMinimumFulfillmentTime(minimumTime)
    if (
      minimumTime > 60 &&
      new Date().getDate() !== earliestFulfillmentDate.getDate()
    ) {
      return `${toLocalDateTime(earliestFulfillmentDate, undefined, false)}`
    }
  }
  return `${minimumTime} - ${minimumTime + 5} min`
}

export const getMinimumFulfillmentTime = (minimumTime: number): Date => {
  const now = new Date()
  return new Date(
    new Date(now).setHours(now.getHours(), now.getMinutes() + minimumTime)
  )
}
